/**
 * Polygon geometry with coordinate rings
 */
interface PolygonGeometry {
  rings: number[][][];
}

/**
 * Calculate the number of tiles needed for an AOI at specific zoom levels
 */
export class TileCalculationService {
  /**
   * Convert latitude to tile Y coordinate
   */
  private static lat2tile(lat: number, zoom: number): number {
    return Math.floor(
      ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
        Math.pow(2, zoom)
    );
  }

  /**
   * Convert longitude to tile X coordinate
   */
  private static lon2tile(lon: number, zoom: number): number {
    return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  }

  /**
   * Calculate area of polygon in square kilometers
   */
  private static calculateAreaKm2(rings: number[][][]): number {
    if (!rings || rings.length === 0) return 0;

    // Use the first ring (exterior ring)
    const ring = rings[0];
    if (!ring || ring.length < 3) return 0;

    // Simple area calculation using shoelace formula
    // For more accuracy, we should use geodesic calculations
    let area = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      area += x1 * y2 - x2 * y1;
    }
    area = Math.abs(area) / 2;

    // Convert from degrees to km² (approximate)
    // At equator: 1 degree ≈ 111 km
    const kmPerDegree = 111;
    return area * kmPerDegree * kmPerDegree;
  }

  /**
   * Get bounding box from polygon rings
   */
  private static getBounds(rings: number[][][]): { minLon: number; maxLon: number; minLat: number; maxLat: number } {
    if (!rings || rings.length === 0) {
      return { minLon: 0, maxLon: 0, minLat: 0, maxLat: 0 };
    }

    const ring = rings[0];
    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    for (const [lon, lat] of ring) {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }

    return { minLon, maxLon, minLat, maxLat };
  }

  /**
   * Calculate tile count for a zoom level
   */
  private static calculateTilesForZoom(bounds: ReturnType<typeof TileCalculationService.getBounds>, zoom: number): number {
    const { minLon, maxLon, minLat, maxLat } = bounds;

    const minTileX = this.lon2tile(minLon, zoom);
    const maxTileX = this.lon2tile(maxLon, zoom);
    const minTileY = this.lat2tile(maxLat, zoom); // Note: Y is inverted
    const maxTileY = this.lat2tile(minLat, zoom);

    const tilesX = maxTileX - minTileX + 1;
    const tilesY = maxTileY - minTileY + 1;

    return tilesX * tilesY;
  }

  /**
   * Calculate total tiles for an AOI across zoom range
   */
  static calculateTileCount(geometry: PolygonGeometry, minZoom: number, maxZoom: number): {
    totalTiles: number;
    tilesByZoom: Record<number, number>;
    areaKm2: number;
    bounds: ReturnType<typeof TileCalculationService.getBounds>;
  } {
    // Convert ArcGIS polygon to simple coordinate array
    const rings = geometry.rings;

    const bounds = this.getBounds(rings);
    const areaKm2 = this.calculateAreaKm2(rings);

    const tilesByZoom: Record<number, number> = {};
    let totalTiles = 0;

    // Calculate tiles for each zoom level
    for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
      const tilesForZoom = this.calculateTilesForZoom(bounds, zoom);
      tilesByZoom[zoom] = tilesForZoom;
      totalTiles += tilesForZoom;
    }

    return {
      totalTiles,
      tilesByZoom,
      areaKm2,
      bounds
    };
  }

  /**
   * Estimate file size for MBTiles
   */
  static estimateFileSize(tileCount: number): number {
    // Average tile size: ~25KB for satellite imagery
    const avgTileSizeBytes = 25 * 1024;
    return tileCount * avgTileSizeBytes;
  }

  /**
   * Estimate generation time
   */
  static estimateGenerationTime(tileCount: number): number {
    // Roughly 1000 tiles per second with our current setup
    const tilesPerSecond = 1000;
    return Math.ceil(tileCount / tilesPerSecond);
  }
}
